import { AnimatePresence, motion } from "framer-motion";
import * as React from "react";
import { createPortal } from "react-dom";
import { localCardImage, showPlaceholderOnError } from "@/lib/card-images";

const BURST_COLORS = ["#fdf8e7", "#e3ad33", "#f3d98a", "#3d9be9", "#ef4a32", "#2fa65a"];

interface Flight {
  readonly id: number;
  readonly from: DOMRect;
  readonly to: DOMRect;
  readonly image: string | null;
  readonly targetId: string;
}

interface Burst {
  readonly id: number;
  readonly x: number;
  readonly y: number;
  readonly color?: string;
}

interface FlightContextValue {
  /** Fly a card image from an element to a registered target, then burst. */
  launch: (input: { from: Element; image: string | null; targetId: string }) => void;
  burst: (x: number, y: number, color?: string) => void;
  register: (id: string, element: HTMLElement | null) => void;
  /** Increments each time a flight lands on the target (for pulse effects). */
  landings: Readonly<Record<string, number>>;
}

const FlightContext = React.createContext<FlightContextValue | null>(null);

export function CardFlightProvider({ children }: { children: React.ReactNode }) {
  const targets = React.useRef(new Map<string, HTMLElement>());
  const [flights, setFlights] = React.useState<Flight[]>([]);
  const [bursts, setBursts] = React.useState<Burst[]>([]);
  const [landings, setLandings] = React.useState<Record<string, number>>({});

  const burst = React.useCallback((x: number, y: number, color?: string) => {
    const id = Date.now() + Math.random();
    setBursts((current) => [...current, { id, x, y, color }]);
    setTimeout(() => setBursts((current) => current.filter((b) => b.id !== id)), 1000);
  }, []);

  const launch = React.useCallback<FlightContextValue["launch"]>(
    ({ from, image, targetId }) => {
      const target = targets.current.get(targetId);
      const fromRect = from.getBoundingClientRect();
      if (!target) {
        burst(fromRect.left + fromRect.width / 2, fromRect.top + fromRect.height / 2);
        return;
      }
      setFlights((current) => [
        ...current,
        {
          id: Date.now() + Math.random(),
          from: fromRect,
          to: target.getBoundingClientRect(),
          image: localCardImage(image),
          targetId,
        },
      ]);
    },
    [burst],
  );

  const register = React.useCallback((id: string, element: HTMLElement | null) => {
    if (element) {
      targets.current.set(id, element);
    } else {
      targets.current.delete(id);
    }
  }, []);

  const land = (flight: Flight) => {
    setFlights((current) => current.filter((f) => f.id !== flight.id));
    const target = targets.current.get(flight.targetId)?.getBoundingClientRect() ?? flight.to;
    burst(target.left + target.width / 2, target.top + target.height / 2);
    setLandings((current) => ({
      ...current,
      [flight.targetId]: (current[flight.targetId] ?? 0) + 1,
    }));
  };

  const value = React.useMemo(
    () => ({ launch, burst, register, landings }),
    [launch, burst, register, landings],
  );

  return (
    <FlightContext.Provider value={value}>
      {children}
      {createPortal(
        <div className="pointer-events-none fixed inset-0 z-[60]">
          <AnimatePresence>
            {flights.map((flight) => {
              const endWidth = 40;
              const endX = flight.to.left + flight.to.width / 2 - endWidth / 2;
              const endY = flight.to.top + flight.to.height / 2 - (endWidth * 1.4) / 2;
              return (
                <motion.div
                  key={flight.id}
                  className="absolute left-0 top-0 overflow-hidden rounded-xl shadow-glow-lg"
                  initial={{
                    x: flight.from.left,
                    y: flight.from.top,
                    width: flight.from.width,
                    height: flight.from.height,
                    rotate: 0,
                    opacity: 1,
                  }}
                  animate={{
                    x: [flight.from.left, (flight.from.left + endX) / 2, endX],
                    y: [flight.from.top, Math.min(flight.from.top, endY) - 120, endY],
                    width: [flight.from.width, flight.from.width * 0.7, endWidth],
                    height: [flight.from.height, flight.from.height * 0.7, endWidth * 1.4],
                    rotate: [0, -18, 340],
                    opacity: [1, 1, 0.2],
                  }}
                  transition={{ duration: 0.85, ease: [0.55, 0, 0.3, 1] }}
                  onAnimationComplete={() => land(flight)}
                >
                  {flight.image ? (
                    <img
                      src={flight.image}
                      alt=""
                      onError={showPlaceholderOnError}
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <div className="h-full w-full bg-gradient-to-br from-gold-300 to-gold-700" />
                  )}
                </motion.div>
              );
            })}
          </AnimatePresence>
          {bursts.map((b) => (
            <ParticleBurst key={b.id} x={b.x} y={b.y} color={b.color} />
          ))}
        </div>,
        document.body,
      )}
    </FlightContext.Provider>
  );
}

export function useCardFlight(): FlightContextValue {
  const context = React.useContext(FlightContext);
  if (!context) {
    throw new Error("useCardFlight must be used inside CardFlightProvider");
  }
  return context;
}

/** Ref callback that registers an element as a flight target. */
export function useFlightTarget(id: string): {
  ref: (element: HTMLElement | null) => void;
  landings: number;
} {
  const { register, landings } = useCardFlight();
  const ref = React.useCallback(
    (element: HTMLElement | null) => register(id, element),
    [id, register],
  );
  return { ref, landings: landings[id] ?? 0 };
}

export function ParticleBurst({
  x,
  y,
  color,
  count = 22,
}: {
  x: number;
  y: number;
  color?: string;
  count?: number;
}) {
  const particles = React.useMemo(
    () =>
      Array.from({ length: count }, (_, index) => {
        const angle = (index / count) * Math.PI * 2 + Math.random() * 0.4;
        const distance = 40 + Math.random() * 70;
        return {
          dx: Math.cos(angle) * distance,
          dy: Math.sin(angle) * distance,
          size: 3 + Math.random() * 5,
          color: color ?? BURST_COLORS[index % BURST_COLORS.length] ?? "#e3ad33",
        };
      }),
    [count, color],
  );
  return (
    <div className="absolute" style={{ left: x, top: y }}>
      <motion.div
        className="absolute -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-gold-200"
        initial={{ width: 0, height: 0, opacity: 0.9 }}
        animate={{ width: 140, height: 140, opacity: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
      />
      {particles.map((particle, index) => (
        <motion.span
          key={index}
          className="absolute rounded-full"
          style={{
            width: particle.size,
            height: particle.size,
            marginLeft: -particle.size / 2,
            marginTop: -particle.size / 2,
            background: particle.color,
            boxShadow: `0 0 10px 2px ${particle.color}`,
          }}
          initial={{ x: 0, y: 0, opacity: 1, scale: 1 }}
          animate={{ x: particle.dx, y: particle.dy, opacity: 0, scale: 0.2 }}
          transition={{ duration: 0.75, ease: [0.2, 0.8, 0.4, 1] }}
        />
      ))}
    </div>
  );
}
